import React, { useEffect, useMemo, useRef, useState } from "react";

// import { matchPath } from "react-router-dom";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Chip, Divider, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useContentContext } from "components/Documents/Content/content-context";
import { SectionContextMenu } from "components/Documents/Menus/SectionContextMenu";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
// import { Routes } from "constants/routes";
import { usePrompt } from "hooks";
import { DocumentMeeting } from "store/types";

import { CreateSectionManualContent } from "./CreateSectionManualContent";

interface Props {
  meeting: DocumentMeeting;
  documentId?: number;
  sectionId?: number;
  menuAvailable?: boolean;
  isUpdateAvailable?: boolean;
}

export const TruncatedParticipants: React.FC<{ participants: string[] }> = ({ participants }) => {
  const [visibleParticipants, setVisibleParticipants] = useState<string[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const calculateVisibleParticipants = () => {
      if (!containerRef.current || !textRef.current || participants.length === 0) return;

      const containerWidth = containerRef.current.offsetWidth;
      let totalWidth = 0;
      let visibleCount = 0;
      const tempDiv = document.createElement("div");
      tempDiv.style.visibility = "hidden";
      tempDiv.style.position = "absolute";
      tempDiv.style.fontSize = "14px";
      document.body.appendChild(tempDiv);

      for (let i = 0; i < participants.length; i++) {
        const participant = participants[i];
        tempDiv.textContent = i === 0 ? participant : `, ${participant}`;
        totalWidth += tempDiv.offsetWidth;

        if (totalWidth + (i < participants.length - 1 ? 50 : 0) <= containerWidth) {
          visibleCount = i + 1;
        } else {
          break;
        }
      }

      document.body.removeChild(tempDiv);
      setVisibleParticipants(participants.slice(0, visibleCount));
    };

    calculateVisibleParticipants();
    window.addEventListener("resize", calculateVisibleParticipants);
    return () => window.removeEventListener("resize", calculateVisibleParticipants);
  }, [participants]);

  const remainingCount = participants.length - visibleParticipants.length;

  return (
    <div ref={containerRef}>
      <Tooltip title={participants.join(", ")} placement="bottom-start">
        <Typography ref={textRef} sx={{ fontSize: "14px", fontWeight: "400", color: "#0A3142" }}>
          {visibleParticipants.join(", ")}
          {Boolean(remainingCount) && (
            <Chip
              label={`+${remainingCount}`}
              size="small"
              sx={{ fontSize: "13px", fontWeight: "400", color: "#0A3142", height: "15px", marginLeft: "5px" }}
            />
          )}
        </Typography>
      </Tooltip>
    </div>
  );
};

export const MeetingHeader: React.FC<Props> = ({ meeting, sectionId, menuAvailable, isUpdateAvailable }) => {
  const { deleteSection, updateMeetingSection } = useContentContext();
  const openPrompt = usePrompt();

  const formatUTCToLocalTime = (timeStr: string) => {
    const [hours, minutes] = timeStr.split(":").map(Number);

    const date = new Date(meeting.meeting_date);

    const utcDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), hours, minutes));

    return new Intl.DateTimeFormat(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    }).format(utcDate);
  };

  const formattedDate = new Intl.DateTimeFormat(undefined, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  }).format(new Date(meeting.meeting_date));
  const formattedStart = formatUTCToLocalTime(meeting.meeting_time_start);
  const formattedEnd = formatUTCToLocalTime(meeting.meeting_time_end);

  const isAvailable = useMemo(() => isUpdateAvailable && menuAvailable, [isUpdateAvailable, menuAvailable]);

  const menuRef: React.RefObject<SVGSVGElement> = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setMenuOpen(true);
  };

  const onEditHandler = async () => {
    setMenuOpen(false);
    const data = await openPrompt({
      Component: CreateSectionManualContent,
      componentType: "modal",
      props: {
        meeting,
        isEdit: true
      }
    });

    if (!data) return;
    await updateMeetingSection({
      meetingId: meeting.id,
      body: {
        ...data
      }
    });
  };

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      sx={{ width: "100%", backgroundColor: "#EDF1F980", p: "10px", borderRadius: "10px" }}
    >
      <Stack direction="column" sx={{ width: "50%" }}>
        <MaterialTooltip title={meeting.title} isShow={true}>
          <Typography
            sx={{
              maxWidth: "250px",
              fontSize: "14px",
              fontWeight: "700",
              color: "#0A3142",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis"
            }}
          >
            {meeting.title}
          </Typography>
        </MaterialTooltip>
        <Typography sx={{ fontSize: "14px", fontWeight: "400", color: "#5D7782" }}>
          {formattedDate} {formattedStart} - {formattedEnd}
        </Typography>
      </Stack>
      <Divider
        orientation="vertical"
        sx={{
          margin: "0px 10px",
          borderRightWidth: 2,
          borderColor: "#BAC7CE",
          height: "auto"
        }}
      />

      <Stack direction="column" sx={{ width: "50%" }}>
        <Stack direction="row" justifyContent="space-between">
          <Typography sx={{ fontSize: "14px", fontWeight: "700", color: "#0A3142" }}>Attendees</Typography>
          {isAvailable && (
            <IconButton sx={{ padding: 0, width: "25px", height: "25px" }} onClick={handleContextMenu}>
              <MoreVertIcon ref={menuRef} />
            </IconButton>
          )}
        </Stack>
        <TruncatedParticipants participants={meeting.participants} />
      </Stack>
      {isAvailable && (
        <SectionContextMenu
          anchorEl={menuRef.current}
          onClose={() => setMenuOpen(false)}
          open={menuOpen}
          onDelete={() => {
            sectionId && deleteSection({ sectionId, isAiMeeting: meeting.is_ai });
            setMenuOpen(false);
          }}
          {...(!meeting.is_ai && { onEdit: onEditHandler })}
        />
      )}
    </Stack>
  );
};
